import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Check, Calendar, Clock, Megaphone, Trash2, X, CreditCard } from "lucide-react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const CAT_MAP = { waitlist_open: "waitlist", crash_open: "waitlist", booking_confirmed: "booking", booking_reminder: "booking", recurring_booked: "booking", favorite_open: "booking", announcement: "news",
  fee_due: "billing", payment_approaching: "billing", payment_due: "billing", membership_renewed: "billing", membership_lapsed_admin: "billing" };
const catOf = (n) => n.category || CAT_MAP[n.type] || "news";

const TABS = [
  { key: "all", label: "All" },
  { key: "booking", label: "Bookings" },
  { key: "waitlist", label: "Waitlist" },
  { key: "billing", label: "Billing" },
  { key: "news", label: "News" },
];

const CAT_META = {
  booking: { icon: Calendar, label: "Booking", cls: "text-emerald-400" },
  waitlist: { icon: Clock, label: "Waitlist", cls: "text-neon" },
  billing: { icon: CreditCard, label: "Billing", cls: "text-amber-400" },
  news: { icon: Megaphone, label: "News", cls: "text-sky-400" },
};

// Swipe left (or right) past the threshold to dismiss; taps still pass through to children.
const SwipeRow = ({ onDismiss, children, testId }) => {
  const x = useMotionValue(0);
  const bg = useTransform(x, [-140, -40, 0, 40, 140], [1, 0.6, 0, 0.6, 1]);
  const [gone, setGone] = useState(false);
  const fling = (dir) => {
    setGone(true);
    animate(x, dir * (typeof window !== "undefined" ? window.innerWidth : 600), { duration: 0.22 }).then(onDismiss);
  };
  const onDragEnd = (_, info) => {
    const dist = info.offset.x, vel = info.velocity.x;
    if (Math.abs(dist) > 110 || Math.abs(vel) > 600) fling(dist < 0 || (dist === 0 && vel < 0) ? -1 : 1);
    else animate(x, 0, { type: "spring", stiffness: 500, damping: 40 });
  };
  return (
    <motion.div layout className="relative overflow-hidden border-b border-neutral-900" data-testid={testId}
      initial={false} animate={gone ? { height: 0, opacity: 0 } : { height: "auto", opacity: 1 }} transition={{ duration: 0.2 }}>
      <motion.div aria-hidden="true" style={{ opacity: bg }} className="absolute inset-0 bg-neon flex items-center justify-between px-6 text-black text-[11px] uppercase tracking-[0.2em] font-semibold">
        <span className="inline-flex items-center gap-2"><Trash2 size={16} /> Clear</span>
        <span className="inline-flex items-center gap-2">Clear <Trash2 size={16} /></span>
      </motion.div>
      <motion.div drag="x" dragDirectionLock dragConstraints={{ left: 0, right: 0 }} dragElastic={0.9} onDragEnd={onDragEnd} style={{ x }}
        className="relative bg-[#0A0A0A] touch-pan-y">
        {children}
      </motion.div>
    </motion.div>
  );
};

export const NotificationBell = ({ compact = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [], unread_by_category: {} });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [anchor, setAnchor] = useState({ top: 72, right: 16 });
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const h = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/me/notifications"); setData(data); } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    load();
    const id = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r) setAnchor({ top: Math.round(r.bottom + 8), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const prev = document.body.style.overflow;
    if (window.matchMedia("(max-width: 639px)").matches) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!user || user.role === "admin") return null;

  const cnt = data.unread_by_category || {};
  const items = (data.items || []).filter((n) => tab === "all" || catOf(n) === tab);

  const markAll = async () => {
    const ids = items.map((n) => n.notification_id);
    try {
      await api.post("/me/notifications/read", tab === "all" ? {} : { ids });
      setData((d) => {
        const readSet = new Set(ids);
        const nextItems = d.items.map((n) => (tab === "all" || readSet.has(n.notification_id)) ? { ...n, read: true } : n);
        const byCat = { all: 0, booking: 0, waitlist: 0, billing: 0, news: 0 };
        nextItems.forEach((n) => { if (!n.read) { byCat.all++; byCat[catOf(n)] = (byCat[catOf(n)] || 0) + 1; } });
        return { ...d, unread: byCat.all, items: nextItems, unread_by_category: byCat };
      });
    } catch { /* silent */ }
  };
  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post("/me/notifications/read", { ids: [n.notification_id] }); } catch { /* silent */ }
      setData((d) => {
        const nextItems = d.items.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x);
        const byCat = { all: 0, booking: 0, waitlist: 0, billing: 0, news: 0 };
        nextItems.forEach((x) => { if (!x.read) { byCat.all++; byCat[catOf(x)] = (byCat[catOf(x)] || 0) + 1; } });
        return { ...d, unread: byCat.all, items: nextItems, unread_by_category: byCat };
      });
    }
    const link = n.link || "/account";
    if (n.meta?.open_mat && n.meta.class_id) {
      sessionStorage.setItem("sculpt:open-mat", JSON.stringify({ class_id: n.meta.class_id, date: n.meta.date }));
      if (window.location.pathname !== "/account") navigate("/");
      setTimeout(() => window.dispatchEvent(new Event("sculpt:open-mat")), 50);
      return;
    }
    if (link.startsWith("/#")) { navigate("/"); setTimeout(() => { document.getElementById(link.slice(2))?.scrollIntoView({ behavior: "smooth" }); }, 250); }
    else navigate(link);
  };

  const recount = (nextItems) => {
    const byCat = { all: 0, booking: 0, waitlist: 0, billing: 0, news: 0 };
    nextItems.forEach((x) => { if (!x.read) { byCat.all++; byCat[catOf(x)] = (byCat[catOf(x)] || 0) + 1; } });
    return byCat;
  };

  const clearRead = async () => {
    try {
      await api.post("/me/notifications/clear", {});
      setData((d) => ({ ...d, items: d.items.filter((n) => !n.read) }));
    } catch { /* silent */ }
  };

  const clearAll = async () => {
    const ids = tab === "all" ? null : items.map((n) => n.notification_id);
    try {
      await api.post("/me/notifications/clear-all", ids ? { ids } : {});
      setData((d) => {
        const nextItems = ids ? d.items.filter((n) => !ids.includes(n.notification_id)) : [];
        const byCat = recount(nextItems);
        return { ...d, items: nextItems, unread: byCat.all, unread_by_category: byCat };
      });
    } catch { /* silent */ }
  };

  const dismiss = async (e, n) => {
    e?.stopPropagation();
    setData((d) => {
      const nextItems = d.items.filter((x) => x.notification_id !== n.notification_id);
      const byCat = recount(nextItems);
      return { ...d, items: nextItems, unread: byCat.all, unread_by_category: byCat };
    });
    try { await api.delete(`/me/notifications/${n.notification_id}`); } catch { /* silent */ }
  };

  const tabHasUnread = items.some((n) => !n.read);
  const hasRead = (data.items || []).some((n) => n.read);
  const tabLabel = TABS.find((t) => t.key === tab)?.label.toLowerCase();

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell" aria-label={`Notifications${data.unread ? ` — ${data.unread} unread` : ""}`} aria-expanded={open}
        className={`relative inline-flex items-center justify-center border transition-colors duration-200 ${compact ? "p-2" : "p-3"} ${data.unread ? "border-neon text-neon" : "border-neutral-700 text-neutral-300 hover:border-neon hover:text-neon"}`}>
        {data.unread ? <BellRing size={14} /> : <Bell size={14} />}
        {data.unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-neon text-black text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && createPortal(
        <>
          <div data-testid="notif-backdrop" onClick={() => setOpen(false)} aria-hidden="true"
            className="fixed inset-0 z-[998] bg-black/70 sm:bg-transparent" />
          <div data-testid="notif-panel" role="dialog" aria-label="Notifications" ref={panelRef}
            style={isDesktop ? { top: anchor.top, right: anchor.right } : undefined}
            className="fixed inset-x-0 bottom-0 top-[8dvh] z-[999] flex flex-col bg-[#0A0A0A] border-t border-neutral-800 shadow-2xl
                       sm:inset-x-auto sm:bottom-auto sm:w-[400px] sm:max-h-[80vh] sm:border">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-300 flex items-center gap-2">
                <BellRing size={14} className="text-neon" /> Notifications
                {data.unread > 0 && <span className="text-neon">· {data.unread} new</span>}
              </p>
              <button onClick={() => setOpen(false)} data-testid="notif-close" aria-label="Close notifications"
                className="inline-flex h-10 w-10 items-center justify-center text-neutral-400 hover:text-white -mr-2 touch-manipulation">
                <X size={20} />
              </button>
            </div>
            <div data-testid="notif-tabs" className="flex items-center gap-1 px-3 py-2 border-b border-neutral-900 overflow-x-auto">
              {TABS.map((t) => {
                const c = cnt[t.key] || 0;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`notif-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs uppercase tracking-wider whitespace-nowrap transition-colors touch-manipulation ${active ? "bg-neon text-black font-semibold" : "text-neutral-400 hover:text-white"}`}>
                    {t.label}
                    {c > 0 && (
                      <span data-testid={`notif-tab-count-${t.key}`} className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${active ? "bg-black text-neon" : "bg-neon text-black"}`}>
                        {c > 9 ? "9+" : c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div data-testid="notif-empty" className="px-6 py-14 text-center">
                  <Bell size={28} className="mx-auto text-neutral-700 mb-3" />
                  <p className="text-sm text-neutral-400">
                    {tab === "all" ? "You're all caught up. We'll alert you here when a waitlisted mat opens." : `No ${tabLabel} notifications.`}
                  </p>
                </div>
              ) : (<>
                <p className="sm:hidden px-5 pt-2 text-[10px] uppercase tracking-wider text-neutral-600 text-center">Swipe an alert sideways to clear it</p>
                {items.map((n) => {
                const meta = CAT_META[catOf(n)] || CAT_META.news;
                const Icon = meta.icon;
                return (
                  <SwipeRow key={n.notification_id} testId={`notif-row-${n.notification_id}`} onDismiss={() => dismiss(null, n)}>
                  <div className={`flex items-stretch ${n.read ? "" : "bg-neon/5"}`}>
                    <button onClick={() => openItem(n)} data-testid={`notif-item-${n.notification_id}`}
                      className="flex-1 min-w-0 text-left px-5 py-4 hover:bg-neutral-900/60 transition-colors touch-manipulation">
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-neon"}`} aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Icon size={12} className={`${meta.cls} shrink-0`} aria-hidden="true" />
                            <span className={`text-[10px] uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                            <span className="text-[10px] uppercase tracking-wider text-neutral-600 ml-auto">{ago(n.created_at)}</span>
                          </div>
                          <p className={`text-[15px] mt-1 leading-snug ${n.read ? "text-neutral-300" : "text-white font-medium"}`}>{n.title}</p>
                          <p className="text-sm text-neutral-400 mt-1 leading-relaxed">{n.body}</p>
                        </div>
                      </div>
                    </button>
                    <button onClick={(e) => dismiss(e, n)} data-testid={`notif-dismiss-${n.notification_id}`} aria-label="Dismiss notification"
                      className="w-12 shrink-0 flex items-center justify-center text-neutral-600 hover:text-neon hover:bg-neutral-900/60 border-l border-neutral-900 transition-colors touch-manipulation">
                      <X size={16} />
                    </button>
                  </div>
                  </SwipeRow>
                );
              })}
              </>)}
            </div>
            {(data.items || []).length > 0 && (
              <div className="border-t border-neutral-900 px-4 py-3 grid grid-cols-2 gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                <button onClick={markAll} disabled={!tabHasUnread} data-testid="notif-mark-all"
                  className="inline-flex items-center justify-center gap-2 border border-neutral-700 text-neutral-200 text-[11px] uppercase tracking-[0.15em] py-3.5 hover:border-neon hover:text-neon transition-colors disabled:opacity-40 disabled:hover:border-neutral-700 disabled:hover:text-neutral-200 touch-manipulation">
                  <Check size={14} /> Mark {tab === "all" ? "all" : tabLabel} read
                </button>
                <button onClick={clearAll} disabled={items.length === 0} data-testid="notif-clear-all"
                  className="inline-flex items-center justify-center gap-2 bg-neon text-black font-semibold text-[11px] uppercase tracking-[0.15em] py-3.5 hover:bg-white transition-colors disabled:opacity-40 touch-manipulation">
                  <Trash2 size={14} /> Clear {tab === "all" ? "all" : tabLabel}
                </button>
                {hasRead && (
                  <button onClick={clearRead} data-testid="notif-clear-read"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500 hover:text-neon transition-colors py-1">
                    Clear only read notifications
                  </button>
                )}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
